import { Box, Chip, ListItem } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { getUserClientId } from "~/api/usersTypes/auth0Profile";
import { useAppSelector } from "~/app/store";
import { createProductSearchOptions } from "~/config/searchProductConfig";
import NavSearchInput from "~/features/navbar/NavSearchInput";

import { searchProduct } from "~/lib/helpers";

import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";

import { BinOrProductResult } from "./ManualInventory";
import { createBinSearchOptions } from "./createBinSearchOptions";
import { useSearchBinsByText } from "./useSearchBinsByText";
import { getViewType } from "./viewType";

type Props = {
  handleAutocompleteSelect: (result: BinOrProductResult) => void;
};

export function ManualInventorySearch({ handleAutocompleteSelect }: Props) {
  const trackedPageName = useMixpanelPageName();
  const { t } = useTranslation();
  const locationInfo = useLocation();
  const { pathname } = locationInfo;

  const searchBinsByText = useSearchBinsByText();

  const clientId = useAppSelector((state) =>
    getUserClientId(state.login.profile)
  );
  const isAdjustingBins = useAppSelector(
    (state) => state.manualInventory.isAdjustingBins
  );

  const [searchText, setSearchText] = useState<string | null>(null);
  const searchPlaceholder = t("search products or bin");

  const [autocompleteOptions, updateAutocompleteOptions] = useState<
    BinOrProductResult[]
  >([] as BinOrProductResult[]);

  const viewType = getViewType(pathname);

  // clear autocomplete options if view changes from bin to product or vice versa
  useEffect(() => updateAutocompleteOptions([]), [viewType]);

  const autocompleteSearchBinsAndProducts = async (
    input: string | null
  ): Promise<void> => {
    // search bins
    if (input === null || input === "" || !clientId) {
      updateAutocompleteOptions([]);
    } else {
      const truncatedInput = input.slice(0, 1000); // avoid excessively long search strings
      const [binSearch, hits] = await Promise.all([
        searchBinsByText(truncatedInput),
        searchProduct(truncatedInput)
      ]);

      const hitsAsAutocompleteRecords = createProductSearchOptions({ hits });
      const binsAsAutocompleteRecords = createBinSearchOptions(binSearch);

      updateAutocompleteOptions([
        ...binsAsAutocompleteRecords,
        ...hitsAsAutocompleteRecords
      ]);
    }
  };

  return (
    <Box
      id="inventoryLookup-navbar-searchComponent"
      display="flex"
      flexGrow="1"
      justifyContent="center"
    >
      <NavSearchInput<BinOrProductResult>
        textInput={searchText || ""}
        setTextInput={(text) => setSearchText(text)}
        isAutocomplete
        searchPlaceholder={searchPlaceholder}
        autocompleteSearchCb={(input) => {
          if (input) {
            mixpanelTrack({
              trackedPageName,
              type: "Search",
              label: "Search Product/Bin",
              eventProperties: {
                "Search Text": input
              }
            });
          }
          void autocompleteSearchBinsAndProducts(input);
        }}
        autocompleteOptions={autocompleteOptions}
        autocompleteLabelConstructor={(option: BinOrProductResult): string =>
          option.displayText
        }
        groupBy={(option) => t(option.type)}
        selectCb={(result: BinOrProductResult) => {
          mixpanelTrack({
            trackedPageName,
            type: "Select Search Result",
            label: "Select Product/Bin",
            eventProperties: {
              "Selected Search Result": result.displayText
            }
          });
          handleAutocompleteSelect(result);
        }}
        clearTextOnSelect
        renderOption={(params, option) => {
          // find all bin results that are the same binId
          const matchingOptions = autocompleteOptions.filter(
            (opt) => opt.displayText === option.displayText
          );
          // if there's more than one, display the compartmentId along with the binId
          const shouldShowCompartment =
            option.autostore_compartment_number && matchingOptions.length > 1;
          return (
            <ListItem
              {...params}
              key={`${option.displayText}${
                option.autostore_compartment_number || ""
              }`}
            >
              <Box mr={1}>{option.displayText}</Box>
              {!!shouldShowCompartment &&
                !!option.autostore_compartment_number && (
                  <Chip
                    label={
                      `${t("compartment")}` +
                      ` ${option.autostore_compartment_number}`
                    }
                    size="small"
                    sx={{
                      backgroundColor: "primary.main",
                      color: "primary.contrastText"
                    }}
                  />
                )}
            </ListItem>
          );
        }}
        autofocus={!isAdjustingBins}
        maxLength={1000}
      />
    </Box>
  );
}
